/* eslint-disable */
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'

const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'

const generateCaptcha = (length = 5) => {
  let text = ''
  for (let i = 0; i < length; i++) {
    text += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return text
}

interface Props {
  onVerify: (token: string | null) => void
}

const CustomCaptcha = forwardRef<{ reset: () => void }, Props>(({ onVerify }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [captchaText, setCaptchaText] = useState(generateCaptcha())
  const [input, setInput] = useState('')

  // Expose reset function to parent components
  useImperativeHandle(ref, () => ({
    reset: () => {
      setInput('')
      setCaptchaText(generateCaptcha())
      onVerify(null)
    },
  }))

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#f8f8f8'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.font = 'bold 28px Verdana'
    ctx.textBaseline = 'middle'

    const centerY = canvas.height / 2

    captchaText.split('').forEach((char, i) => {
      const x = 20 + i * 25
      const y = centerY + (Math.random() * 6 - 3) // tiny vertical jitter

      ctx.save()
      ctx.translate(x, y)
      ctx.rotate((Math.random() - 0.5) * 0.5)
      ctx.fillStyle = '#18181B'
      ctx.fillText(char, 0, 0)
      ctx.restore()
    })

    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = `rgba(0,0,0,0.2)`
      ctx.beginPath()
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height)
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height)
      ctx.stroke()
    }
  }, [captchaText])

  const handleVerify = () => {
    if (input === captchaText) {
      onVerify(captchaText)
    } else {
      onVerify(null)
      setInput('')
      setCaptchaText(generateCaptcha())
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        background: '#fafafa',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Canvas + Input side by side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <canvas
          ref={canvasRef}
          width={160}
          height={45}
          style={{
            border: '1px solid #ccc',
            borderRadius: '6px',
            backgroundColor: '#fff',
          }}
        />
        <input
          type="text"
          value={input}
          placeholder="Enter captcha"
          maxLength={5} // prevent typing more than 5 chars
          onChange={e => setInput(e.target.value)}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            border: '1px solid #ccc',
            outline: 'none',
            height: '40px',
            width: '15ch',
            textAlign: 'center', // keep chars centered
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
        <button
          type="button"
          onClick={handleVerify}
          style={{
            padding: '4px 10px',
            fontSize: '13px',
            background: '#4caf50', // green
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseOver={e => (e.currentTarget.style.background = '#43a047')}
          onMouseOut={e => (e.currentTarget.style.background = '#4caf50')}
        >
          Verify
        </button>
        <button
          type="button"
          onClick={() => setCaptchaText(generateCaptcha())}
          style={{
            padding: '4px 10px',
            fontSize: '13px',
            background: '#9e9e9e',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseOver={e => (e.currentTarget.style.background = '#757575')}
          onMouseOut={e => (e.currentTarget.style.background = '#9e9e9e')}
        >
          Refresh
        </button>
      </div>
    </div>
  )
})

CustomCaptcha.displayName = 'CustomCaptcha'

export default CustomCaptcha
